/**
 * PATH: packages/job-hub/src/jobs/qa/qaChecklistRowMenu.ts
 * FILE_ID: JOBHUB-QA-ROWMENU-001
 * PURPOSE: Pure builder for the QA checklist row right-click menu. Returns a
 *          declarative ContextMenuItem[] for the shared <ContextMenu> primitive
 *          so the excel-like table gets native-feeling row actions (insert
 *          above/below, duplicate, hold point, move, delete).
 * WHY:     Operator: "no right click either" — the clay table needed the
 *          spreadsheet right-click affordances, not just footer buttons.
 * FLOW: right-click row -> openAt(e) + remember row -> buildQaChecklistRowMenu(
 *       state, actions) -> <ContextMenu items=…> -> action -> qaApi mutation.
 * RELATED:
 * - @quotech/ui contextMenu (ContextMenu, useContextMenu, ContextMenuItem)
 * - ./QaChecklistTable.tsx (renders it)
 * - ./checklistTable.ts (insertIdAt powers insert/duplicate ordering)
 */
import {
  ArrowDown, ArrowUp, ClipboardPaste, Copy, CornerDownRight, Flag, FlagOff, Plus, ShieldCheck, ShieldOff, Trash2,
} from 'lucide-react'
import type { ContextMenuItem } from '@quotech/ui'

export interface QaRowMenuState {
  /** Row is the first in the table (can't move up). */
  isFirst: boolean
  /** Row is the last in the table (can't move down). */
  isLast: boolean
  /** Row is parked by Hold (toggles the label). */
  hasHold: boolean
  /** Row asks for a hold point signature; it stays in Run and the ITP. */
  needsSignoff?: boolean
  /** Whole table is locked / not a draft — only an explanatory header shows. */
  disabled: boolean
  /** Ticked rows included in Copy, when this row is one of them. */
  copyCount?: number
  /** Clipboard label. Null means nothing of this kind is copied. */
  pasteLabel?: string | null
}

export interface QaRowMenuActions {
  onInsertAbove: () => void
  onInsertBelow: () => void
  onInsertSubRow: () => void
  onDuplicate: () => void
  onToggleHold: () => void
  onToggleSignoff?: () => void
  onMoveUp: () => void
  onMoveDown: () => void
  onDelete: () => void
  onCopy?: () => void
  onPasteAbove?: () => void
  onPasteBelow?: () => void
  onUndo?: () => void
  onRedo?: () => void
}

/**
 * Build the row context menu. Pure I->O: ``state`` decides which entries are
 * disabled / how they read, ``actions`` are the click handlers. When the table
 * is locked we return a single non-actionable header so the operator gets a
 * reason rather than a dead menu.
 */
export function buildQaChecklistRowMenu(
  state: QaRowMenuState,
  actions: QaRowMenuActions,
): ContextMenuItem[] {
  if (state.disabled) {
    const locked: ContextMenuItem[] = [{ id: 'locked', kind: 'header', label: 'Checklist is locked' }]
    if (actions.onCopy) {
      locked.push({ id: 'copy-row', label: 'Copy row', icon: Copy, onSelect: actions.onCopy })
    }
    return locked
  }
  const history: ContextMenuItem[] = [
    { id: 'undo', label: 'Undo', onSelect: actions.onUndo, disabled: !actions.onUndo },
    { id: 'redo', label: 'Redo', onSelect: actions.onRedo, disabled: !actions.onRedo },
    { id: 'sep-history', kind: 'separator' },
  ]
  const clip: ContextMenuItem[] = actions.onCopy
    ? [
      { id: 'sep-clip', kind: 'separator' },
      {
        id: 'copy-row',
        label: (state.copyCount ?? 1) > 1 ? `Copy ${state.copyCount} rows` : 'Copy row',
        icon: Copy,
        onSelect: actions.onCopy,
      },
      {
        id: 'paste-above',
        label: 'Paste above',
        icon: ClipboardPaste,
        hint: state.pasteLabel ?? undefined,
        disabled: !state.pasteLabel,
        onSelect: actions.onPasteAbove,
      },
      {
        id: 'paste-below',
        label: 'Paste below',
        icon: ClipboardPaste,
        hint: state.pasteLabel ?? undefined,
        disabled: !state.pasteLabel,
        onSelect: actions.onPasteBelow,
      },
    ]
    : []
  return [
    ...history,
    { id: 'insert-above', label: 'Insert row above', icon: Plus, onSelect: actions.onInsertAbove },
    { id: 'insert-below', label: 'Insert row below', icon: Plus, onSelect: actions.onInsertBelow },
    { id: 'insert-subrow', label: 'Insert sub-row', icon: CornerDownRight, onSelect: actions.onInsertSubRow },
    { id: 'duplicate', label: 'Duplicate row', icon: Copy, onSelect: actions.onDuplicate },
    ...clip,
    { id: 'sep-hold', kind: 'separator' },
    {
      id: 'toggle-hold',
      label: state.hasHold ? 'Remove hold point' : 'Mark as hold point',
      icon: state.hasHold ? FlagOff : Flag,
      onSelect: actions.onToggleHold,
    },
    ...(actions.onToggleSignoff
      ? [{
        id: 'toggle-signoff',
        label: state.needsSignoff ? 'Remove sign-off' : 'Needs sign-off',
        icon: state.needsSignoff ? ShieldOff : ShieldCheck,
        onSelect: actions.onToggleSignoff,
      }]
      : []),
    { id: 'move-up', label: 'Move up', icon: ArrowUp, disabled: state.isFirst, onSelect: actions.onMoveUp },
    { id: 'move-down', label: 'Move down', icon: ArrowDown, disabled: state.isLast, onSelect: actions.onMoveDown },
    { id: 'sep-delete', kind: 'separator' },
    { id: 'delete', kind: 'destructive', label: 'Delete row', icon: Trash2, onSelect: actions.onDelete },
  ]
}
